import _set from "lodash.set";

type ResumeDataLike = Record<string, any>;

export type EditableResumeField = {
  sectionId: string;
  sectionName: string;
  itemLabel: string;
  fieldPath: string;
  value: string;
};

export type OptimizationSuggestion = {
  id: string;
  sectionId: string;
  sectionName: string;
  itemLabel: string;
  fieldPath: string;
  previousText: string;
  suggestedText: string;
};

export type OptimizationSuggestionGroup = {
  sectionId: string;
  sectionName: string;
  suggestions: OptimizationSuggestion[];
};

export type OptimizationSuggestionDrafts = Record<string, string>;

const stripHtml = (value: unknown) =>
  typeof value === "string"
    ? value
        .replace(/<[^>]+>/g, " ")
        .replace(/\s+/g, " ")
        .trim()
    : "";

const getUrlLabel = (value: unknown) => {
  if (typeof value === "string") return value;
  if (value && typeof value === "object") {
    const url = value as { label?: string; href?: string };
    return url.label || url.href || "";
  }

  return "";
};

const getItemLabel = (item: Record<string, any>, fallback: string) => {
  const primary =
    getUrlLabel(item.company) ||
    getUrlLabel(item.institution) ||
    item.name ||
    item.organization ||
    item.title ||
    item.network ||
    fallback;
  const secondary = item.position || item.area || item.description || item.issuer || item.username;

  return [primary, secondary].filter(Boolean).join(" - ");
};

const toSuggestionId = (fieldPath: string) =>
  fieldPath.replace(/[^a-zA-Z0-9]+/g, "-").replace(/^-|-$/g, "");

export const buildEditableResumeFields = (resumeData: ResumeDataLike): EditableResumeField[] => {
  const fields: EditableResumeField[] = [];
  const sections = resumeData.sections ?? {};

  if (stripHtml(sections.summary?.content)) {
    fields.push({
      sectionId: "summary",
      sectionName: sections.summary.name || "Summary",
      itemLabel: "Professional Summary",
      fieldPath: "sections.summary.content",
      value: sections.summary.content,
    });
  }

  for (const [sectionId, section] of Object.entries<Record<string, any>>(sections)) {
    if (sectionId === "summary" || sectionId === "custom") continue;
    if (!Array.isArray(section.items)) continue;

    section.items.forEach((item: Record<string, any>, index: number) => {
      const fieldName = stripHtml(item.summary)
        ? "summary"
        : stripHtml(item.description)
          ? "description"
          : "";
      if (!fieldName) return;

      fields.push({
        sectionId,
        sectionName: section.name || sectionId,
        itemLabel: getItemLabel(item, `${section.name || sectionId} ${index + 1}`),
        fieldPath: `sections.${sectionId}.items[${index}].${fieldName}`,
        value: item[fieldName],
      });
    });
  }

  for (const [sectionId, section] of Object.entries<Record<string, any>>(sections.custom ?? {})) {
    if (!Array.isArray(section.items)) continue;

    section.items.forEach((item: Record<string, any>, index: number) => {
      const fieldName = stripHtml(item.summary)
        ? "summary"
        : stripHtml(item.description)
          ? "description"
          : "";
      if (!fieldName) return;

      fields.push({
        sectionId: `custom.${sectionId}`,
        sectionName: section.name || "Custom Section",
        itemLabel: getItemLabel(item, `${section.name || "Custom Section"} ${index + 1}`),
        fieldPath: `sections.custom.${sectionId}.items[${index}].${fieldName}`,
        value: item[fieldName],
      });
    });
  }

  return fields;
};

const parseJsonPayload = (value: string) => {
  const trimmed = value.trim();
  const fenced = trimmed.match(/```(?:json)?\s*([\s\S]*?)```/i);
  const source = fenced?.[1] ?? trimmed;
  return JSON.parse(source) as { suggestions?: unknown };
};

export const parseOptimizationSuggestions = (value: string): OptimizationSuggestion[] => {
  const payload = parseJsonPayload(value);
  if (!Array.isArray(payload.suggestions)) return [];

  return payload.suggestions
    .map((entry): OptimizationSuggestion | null => {
      if (!entry || typeof entry !== "object") return null;
      const suggestion = entry as Partial<OptimizationSuggestion>;
      const fieldPath = String(suggestion.fieldPath ?? "").trim();
      const previousText = String(suggestion.previousText ?? "").trim();
      const suggestedText = String(suggestion.suggestedText ?? "").trim();

      if (!fieldPath || !previousText || !suggestedText || previousText === suggestedText)
        return null;

      return {
        id: toSuggestionId(fieldPath),
        sectionId: String(suggestion.sectionId ?? "resume"),
        sectionName: String(suggestion.sectionName ?? "Resume"),
        itemLabel: String(suggestion.itemLabel ?? "Content"),
        fieldPath,
        previousText,
        suggestedText,
      };
    })
    .filter((suggestion): suggestion is OptimizationSuggestion => Boolean(suggestion));
};

export const applyOptimizationSuggestion = <T extends ResumeDataLike>(
  resumeData: T,
  suggestion: OptimizationSuggestion,
) => {
  const nextData = JSON.parse(JSON.stringify(resumeData)) as T;
  _set(nextData, suggestion.fieldPath, suggestion.suggestedText);

  return nextData;
};

export const applyOptimizationSuggestions = <T extends ResumeDataLike>(
  resumeData: T,
  suggestions: OptimizationSuggestion[],
) => {
  const nextData = JSON.parse(JSON.stringify(resumeData)) as T;

  for (const suggestion of suggestions) {
    _set(nextData, suggestion.fieldPath, suggestion.suggestedText);
  }

  return nextData;
};

export const getResolvedOptimizationSuggestions = (
  suggestions: OptimizationSuggestion[],
  acceptedSuggestionIds: Set<string>,
  drafts: OptimizationSuggestionDrafts = {},
) =>
  suggestions
    .filter((suggestion) => acceptedSuggestionIds.has(suggestion.id))
    .map((suggestion) => {
      const editedText = drafts[suggestion.id]?.trim();

      return {
        ...suggestion,
        suggestedText: editedText || suggestion.suggestedText,
      };
    });

export const groupOptimizationSuggestions = (
  suggestions: OptimizationSuggestion[],
): OptimizationSuggestionGroup[] => {
  const groups = new Map<string, OptimizationSuggestionGroup>();

  for (const suggestion of suggestions) {
    const key = suggestion.sectionId;
    const group = groups.get(key);

    if (group) {
      group.suggestions.push(suggestion);
      continue;
    }

    groups.set(key, {
      sectionId: suggestion.sectionId,
      sectionName: suggestion.sectionName,
      suggestions: [suggestion],
    });
  }

  return Array.from(groups.values());
};

export const calculateOptimizationScore = (acceptedCount: number, suggestionCount: number) =>
  suggestionCount > 0 ? Math.round(25 + (acceptedCount / suggestionCount) * 61) : 25;

export const shouldShowOptimizationEntry = (suggestions: OptimizationSuggestion[]) =>
  suggestions.length === 0;
